import languageService from '../../../services/LanguageService'
import { MarketArchetype } from '../../api/markets'

export interface MarketLevelBlueprintGroup {
  label: string
  // null for a blueprint without a resolvable event type, which sorts last.
  sortKey: string | null
  searchText: string
}

export interface EventTypeNameResolver {
  eventTypeName: (typeId: string) => string
  eventCategoryName: (categoryId: string) => string
}

export const languageServiceNameResolver: EventTypeNameResolver = {
  eventTypeName: (typeId) => languageService.getTranslation('tags', typeId),
  eventCategoryName: (categoryId) => languageService.getTranslation('tagGroups', categoryId),
}

export const getBlueprintDisplayName = (blueprint: MarketArchetype): string => blueprint.names.en || blueprint.names.ja || blueprint.names.zh || 'Unnamed'

/**
 * Derives the dropdown group of every blueprint from the event types it accepts, most used first.
 *
 * Returns isGrouped: false when no blueprint carries a resolvable event type, which happens on a
 * backend that predates the eventTypes field. The caller then keeps the flat alphabetical list.
 */
export const groupMarketLevelBlueprints = (
  blueprints: MarketArchetype[],
  { uncategorizedLabel, resolver = languageServiceNameResolver }: { uncategorizedLabel: string; resolver?: EventTypeNameResolver }
): { groupings: Map<string, MarketLevelBlueprintGroup>; isGrouped: boolean } => {
  const groupings = new Map<string, MarketLevelBlueprintGroup>()
  let isGrouped = false

  blueprints.forEach((blueprint) => {
    // The endpoint sorts by count descending, so the first entry is the most used type of the ones
    // the blueprint accepts, or an accepted type nothing uses yet for a blueprint without events. A
    // blueprint that has declared nothing is listed under the type its events use most.
    const [dominant] = blueprint.eventTypes?.length ? blueprint.eventTypes : blueprint.unacceptedEventTypes ?? []
    const dominantCategory = dominant?.categoryId ? resolver.eventCategoryName(dominant.categoryId) : ''
    const dominantType = dominant ? resolver.eventTypeName(dominant.typeId) : ''
    const heading = [dominantCategory, dominantType].filter(Boolean).join(' — ')

    if (heading) {
      isGrouped = true
    }

    groupings.set(blueprint.id, {
      label: heading || uncategorizedLabel,
      sortKey: heading ? heading.toLowerCase() : null,
      // Analysts are dropping the event type prefix from the blueprint names, so matching on the
      // name alone would stop finding a blueprint by its type. Matching only the heading it is
      // listed under keeps every hit explainable, and typing an event type or a category returns
      // exactly that group. Matching the types it merely also uses would pull in blueprints that
      // are listed elsewhere, with nothing on the row to show why.
      searchText: heading.toLowerCase(),
    })
  })

  return { groupings, isGrouped }
}

/**
 * Orders blueprints so that a grouped list renders one heading per group. MUI starts a new heading
 * every time the group of an option differs from the previous one, so groups must be contiguous.
 */
export const sortMarketLevelBlueprints = (
  blueprints: MarketArchetype[],
  groupings: Map<string, MarketLevelBlueprintGroup>,
  isGrouped: boolean
): MarketArchetype[] =>
  [...blueprints].sort((a, b) => {
    if (isGrouped) {
      const sortKeyA = groupings.get(a.id)?.sortKey ?? null
      const sortKeyB = groupings.get(b.id)?.sortKey ?? null

      if (sortKeyA !== sortKeyB) {
        if (sortKeyA === null) return 1
        if (sortKeyB === null) return -1
        return sortKeyA.localeCompare(sortKeyB)
      }
    }

    return getBlueprintDisplayName(a).toLowerCase().localeCompare(getBlueprintDisplayName(b).toLowerCase())
  })
